import { useState } from "react";
import { toast } from "sonner";
import { Mail, MessageSquare, BadgeCheck, Loader2, Send } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return detail.msg || String(detail);
};

function Channel({ channel, label, icon: Icon, target, verified, initial }) {
  const { setUser } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(initial || null);

  const send = async () => {
    setBusy(true);
    try { const r = await api.post("/auth/verify/send", { channel }); setInfo(r.data); toast.success(r.data.delivered ? `Code sent to your ${label.toLowerCase()}` : "Code generated (dev mode)"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try { const r = await api.post("/auth/verify/confirm", { channel, code: code.trim() }); setUser(r.data); toast.success(`${label} verified`); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`verify-${channel}`} className="glass rounded-2xl p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold"><Icon size={15} className="text-cyan-400" /> {label}</div>
        {verified ? <span data-testid={`verified-${channel}`} className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 inline-flex items-center gap-1"><BadgeCheck size={11} /> Verified</span>
          : <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-500/10 text-amber-300">Unverified</span>}
      </div>
      <div className="text-xs text-slate-500 font-mono mt-1">{target || "—"}</div>
      {!verified && (
        <div className="mt-3 space-y-2">
          {info?.dev_code && <div data-testid={`dev-code-${channel}`} className="text-[10px] font-mono text-amber-300 bg-amber-500/10 rounded-lg px-3 py-2">Dev mode (provider not configured yet) · code: <b>{info.dev_code}</b></div>}
          <div className="flex gap-2">
            <input data-testid={`code-input-${channel}`} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" inputMode="numeric"
              className="flex-1 bg-[#0A0C14] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono outline-none focus:border-purple-500/40" />
            <button data-testid={`code-confirm-${channel}`} onClick={confirm} disabled={busy || code.length !== 6} className="px-3 py-2 rounded-lg text-xs font-semibold bg-gradient-to-r from-purple-600 to-cyan-600 text-white disabled:opacity-40">Verify</button>
            <button data-testid={`code-send-${channel}`} onClick={send} disabled={busy} className="px-3 py-2 rounded-lg text-xs border border-white/15 hover:bg-white/5 inline-flex items-center gap-1">{busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} {info ? "Resend" : "Send code"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function VerifyPanel({ user, initial }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Channel channel="email" label="Email" icon={Mail} target={user.email} verified={user.email_verified} initial={initial?.email} />
      <Channel channel="mobile" label="WhatsApp mobile" icon={MessageSquare} target={user.mobile} verified={user.mobile_verified} initial={initial?.mobile} />
    </div>
  );
}
